import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MarginCalculatorMarginService } from 'src/app/margin-calculator/services/margin-calculator-margin.service';
import { selectValidations } from 'src/app/root-store/validations/validations-selectors';
import { ContractRequestService } from 'src/app/shared/lib/contract-request/contract-request.service';
import { MoneyMovementRule } from 'src/app/shared/lib/validations/models/rules/money-movement-rule.model';
import { ValidationRuleInvocationDto } from 'src/app/shared/lib/validations/models/validation-rule-invocation-return.model';
import { ValidationRuleInvocation } from 'src/app/shared/lib/validations/models/validation-rule-invocation.model';
import { ValidationRuleEnum } from 'src/app/shared/lib/validations/models/validation-rule.enum';
import * as ValidationsActions from './validations-actions';

@Injectable()
export class ValidationsEffects {
    constructor(
        private actions$: Actions,
        private _validationsService: MarginCalculatorMarginService,
        private readonly contractRequestService: ContractRequestService,
        private readonly store: Store
    ) {}

    getValidations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ValidationsActions.getValidations),
            switchMap((action) => {
                return this._validationsService.getMarginRuleValidations(action.marginId).pipe(
                    map((result: ValidationRuleInvocationDto[]) =>
                        ValidationsActions.getValidationsSuccess({ validations: result })
                    ),
                    catchError((error) => of(ValidationsActions.getValidationsFailure({ error })))
                );
            })
        );
    });

    saveValidations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ValidationsActions.saveValidations),
            switchMap((action) => {
                return this.contractRequestService.getChangeType(action.userId, action.marginId).pipe(
                    map((changeType) => changeType), //Get the facility and previous margin Id at the same time
                    switchMap((x) => {
                        const previousMarginId = Number(x?.previousMarginId ?? 0);
                        if (previousMarginId > 0) {
                            return this._validationsService.saveMarginRuleValidation(action.marginId, [
                                this.createMoneyMovement(previousMarginId, action.marginId)
                            ]);
                        } else {
                            return of(-1); //If there is no previous margin this is a NEW request.
                        }
                    }),
                    map((validationsSucceeded) => validationsSucceeded),
                    concatLatestFrom(() => [this.store.select(selectValidations)]),
                    switchMap(([x, validations]) => {
                        if (x !== -1) {
                            return of(validations);
                        } else {
                            return of([]);
                        }
                    }),
                    map((validations: ValidationRuleInvocationDto[]) => validations),
                    switchMap((x) => {
                        if (x.filter((y) => y.softOrHardStop).length > 0) {
                            //Missing Requirement
                            return of(-1);
                        }
                        return of(0);
                    }),
                    map(() => ValidationsActions.saveValidationsSuccess()),
                    catchError((error) => of(ValidationsActions.saveValidationsFailure({ error })))
                );
            })
        );
    });

    private createMoneyMovement(previousMarginId: number, marginId: number): ValidationRuleInvocation {
        const dataMoneyMovement: MoneyMovementRule = {
            previousMarginId,
            marginId
        };

        return {
            data: dataMoneyMovement,
            validationType: ValidationRuleEnum.MoneyMovement,
            softOrHardStop: true,
            shouldEmail: false,
            succeededValidation: false,
            createdOn: dayjs().format()
        };
    }
}
